// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Wall footprints that meet at their joins (frontend-5yu), the plan's and
 * the 3D fallback's counterpart of ReUseX `build_solids` (reusex-6x9g): at
 * a corner of walls about as thick (within `BUTT_RATIO`) both are mitred on
 * the line through where their outer faces meet and where their inner ones
 * meet, else the thicker runs through to the thinner's outer face and the
 * thinner abuts it (a butt joint); the stem of a tee abuts the
 * through wall's near face; walls that cross or go on collinear are left as
 * they are. So two walls that meet neither leave a notch nor overlap, at
 * any angle and for any two thicknesses.
 *
 * `GET /walls` carries no joins, so they are read off the geometry: the
 * walls stage snaps a joined end onto the other wall's axis, so an end
 * within `JOIN_TOL` of another wall's end is a corner and one on another
 * wall's path a tee. Pure: no React, no DOM.
 */

export type XY = readonly [number, number];

export interface JoinableWall {
  guid: string;
  start: XY;
  end: XY;
  /** Half the thickness: the footprint is the axis ± this. 0 = a line. */
  half: number;
  /** Walls only join others with the same key (the storey). */
  group?: number | string | null;
}

/** An end this close to another wall's end or axis is joined to it, metres. */
export const JOIN_TOL = 0.05;

type V = [number, number];
const sub = (a: XY, b: XY): V => [a[0] - b[0], a[1] - b[1]];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const cross = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];

interface Frame {
  w: JoinableWall;
  p0: XY;
  d: V;
  n: V;
  len: number;
}

function frameOf(w: JoinableWall): Frame | null {
  const v = sub(w.end, w.start);
  const len = Math.hypot(v[0], v[1]);
  if (!(len > 1e-6)) return null;
  const d: V = [v[0] / len, v[1] / len];
  return { w, p0: w.start, d, n: [-d[1], d[0]], len };
}

const at = (f: Frame, s: number, t: number): V => [f.p0[0] + f.d[0] * s + f.n[0] * t, f.p0[1] + f.d[1] * s + f.n[1] * t];

/** Where the face of `f` at offset `t` meets the line q + u k, as s along `f`. */
function faceMeets(f: Frame, t: number, q: XY, u: XY): number | null {
  const c = cross(f.d, u);
  if (Math.abs(c) < 1e-9) return null;
  return cross(sub(q, at(f, 0, t)), u) / c;
}

const farEnd = (f: Frame, start: boolean): V => (start ? at(f, f.len, 0) : [f.p0[0], f.p0[1]]);
const sideOf = (f: Frame, p: XY) => (dot(f.n, sub(p, f.p0)) >= 0 ? 1 : -1);

/** An end's place: s on the −n face and on the +n face. */
type Cut = { lo: number; hi: number };

const MIN_SIN = Math.sin((10 * Math.PI) / 180);

function mitre(A: Frame, aStart: boolean, B: Frame, bStart: boolean): [Cut, Cut] | null {
  if (Math.abs(cross(A.d, B.d)) < MIN_SIN) return null;
  const bSide = sideOf(A, farEnd(B, bStart));
  const aSide = sideOf(B, farEnd(A, aStart));
  const aToward = bSide * A.w.half;
  const bToward = aSide * B.w.half;
  const aOut = faceMeets(A, -aToward, at(B, 0, -bToward), B.d);
  const aIn = faceMeets(A, aToward, at(B, 0, bToward), B.d);
  const bOut = faceMeets(B, -bToward, at(A, 0, -aToward), A.d);
  const bIn = faceMeets(B, bToward, at(A, 0, aToward), A.d);
  if (aOut === null || aIn === null || bOut === null || bIn === null) return null;
  // The face away from the other wall is −n when the other lies on +n.
  return [bSide > 0 ? { lo: aOut, hi: aIn } : { lo: aIn, hi: aOut }, aSide > 0 ? { lo: bOut, hi: bIn } : { lo: bIn, hi: bOut }];
}

/** Butt corner: the thicker `T` runs through to the thinner `U`'s far face (away from T's body). */
function runThrough(T: Frame, tStart: boolean, U: Frame): Cut | null {
  const away = -sideOf(U, farEnd(T, tStart)) * U.w.half;
  const lo = faceMeets(T, -T.w.half, at(U, 0, away), U.d);
  const hi = faceMeets(T, T.w.half, at(U, 0, away), U.d);
  return lo === null || hi === null ? null : { lo, hi };
}

/** A corner of walls whose thicknesses differ by more than this factor is a butt joint, else a mitre. */
export const BUTT_RATIO = 1.25;

function corner(A: Frame, aStart: boolean, B: Frame, bStart: boolean): [Cut, Cut] | null {
  const ta = A.w.half;
  const tb = B.w.half;
  if (Math.max(ta, tb) <= BUTT_RATIO * Math.min(ta, tb)) return mitre(A, aStart, B, bStart);
  if (Math.abs(cross(A.d, B.d)) < MIN_SIN) return null;
  // The thicker runs through to the thinner's far face; the thinner abuts it.
  if (ta > tb) {
    const t = runThrough(A, aStart, B);
    const u = abut(B, bStart, A);
    return t && u ? [t, u] : null;
  }
  const t = runThrough(B, bStart, A);
  const u = abut(A, aStart, B);
  return t && u ? [u, t] : null;
}

function abut(A: Frame, aStart: boolean, B: Frame): Cut | null {
  const near = sideOf(B, farEnd(A, aStart)) * B.w.half;
  const lo = faceMeets(A, -A.w.half, at(B, 0, near), B.d);
  const hi = faceMeets(A, A.w.half, at(B, 0, near), B.d);
  return lo === null || hi === null ? null : { lo, hi };
}

/**
 * Each wall's footprint, cut at its joins: four corners in the order
 * start +n, end +n, end −n, start −n (n the axis' left normal), or the axis
 * alone (two points, trimmed at a tee) for a wall with no thickness.
 * Walls without length are left out.
 */
export function joinedFootprints(walls: readonly JoinableWall[]): Map<string, XY[]> {
  const frames = walls.map(frameOf).filter((f): f is Frame => f !== null);
  const cuts = new Map<string, Cut>(); // `${i}:${start}`
  const key = (i: number, start: boolean) => `${i}:${start ? 's' : 'e'}`;

  const sane = (i: number, start: boolean, c: Cut) => {
    const f = frames[i];
    const here = start ? 0 : f.len;
    const limit = Math.max(0.5, 12 * f.w.half);
    return (
      Number.isFinite(c.lo) &&
      Number.isFinite(c.hi) &&
      Math.abs(c.lo - here) <= limit &&
      Math.abs(c.hi - here) <= limit &&
      (start ? Math.max(c.lo, c.hi) < f.len - 1e-3 : Math.min(c.lo, c.hi) > 1e-3)
    );
  };
  // An end with several joins: one with a wall that has a thickness beats one with a line (a
  // one-sided wall drawn as its axis close by), then the furthest-reaching wins, so no notch opens.
  const rank = new Map<string, number>();
  let partner = 0;
  const propose = (i: number, start: boolean, c: Cut) => {
    if (!sane(i, start, c)) return;
    const f = frames[i];
    const out = (x: Cut) => (start ? -(x.lo + x.hi) / 2 : (x.lo + x.hi) / 2 - f.len);
    const k = key(i, start);
    const prev = cuts.get(k);
    const r = rank.get(k) ?? -1;
    if (!prev || partner > r || (partner === r && out(c) > out(prev))) {
      cuts.set(k, c);
      rank.set(k, partner);
    }
  };
  const square = (i: number, start: boolean, reach: number) => {
    const s = start ? -reach : frames[i].len + reach;
    propose(i, start, { lo: s, hi: s });
  };

  for (let i = 0; i < frames.length; i += 1) {
    const A = frames[i];
    for (const aStart of [true, false]) {
      const e = aStart ? A.w.start : A.w.end;
      for (let j = 0; j < frames.length; j += 1) {
        if (j === i) continue;
        const B = frames[j];
        if ((A.w.group ?? null) !== (B.w.group ?? null)) continue;
        partner = B.w.half > 0 ? 1 : 0;
        const r = sub(e, B.p0);
        const s = dot(r, B.d);
        const off = Math.abs(dot(r, B.n));
        if (off > JOIN_TOL || s < -JOIN_TOL || s > B.len + JOIN_TOL) continue;
        const bStart = s <= JOIN_TOL ? true : s >= B.len - JOIN_TOL ? false : null;
        if (bStart === null) {
          // On B's path: a tee.
          const c = abut(A, aStart, B);
          if (c && sane(i, aStart, c)) propose(i, aStart, c);
          else square(i, aStart, B.w.half);
          continue;
        }
        // End to end: a corner (each pair is met from both sides; the cut is the same).
        const m = corner(A, aStart, B, bStart);
        if (m && sane(i, aStart, m[0]) && sane(j, bStart, m[1])) {
          propose(i, aStart, m[0]);
        } else {
          // Nearly parallel: straight on, the walls already meet square at the joint (and a tee
          // there, e.g. two stems either side of a through wall, still cuts the end); folded back
          // reaches over B.
          const aOut: V = aStart ? [-A.d[0], -A.d[1]] : A.d;
          const bOut: V = bStart ? [-B.d[0], -B.d[1]] : B.d;
          if (dot(aOut, bOut) >= 0) square(i, aStart, B.w.half);
        }
      }
    }
  }

  const out = new Map<string, XY[]>();
  frames.forEach((f, i) => {
    const s0 = cuts.get(key(i, true)) ?? { lo: 0, hi: 0 };
    const s1 = cuts.get(key(i, false)) ?? { lo: f.len, hi: f.len };
    const h = f.w.half;
    out.set(
      f.w.guid,
      h > 0 ? [at(f, s0.hi, h), at(f, s1.hi, h), at(f, s1.lo, -h), at(f, s0.lo, -h)] : [at(f, (s0.lo + s0.hi) / 2, 0), at(f, (s1.lo + s1.hi) / 2, 0)],
    );
  });
  return out;
}
